import { Badge, Button, Card, Group, Select, Stack, Table } from "@mantine/core";
import { IconPlayerPlay, IconCircleCheck } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import useSWR from "swr";
import { EndCustomerDelinquencyApi } from "../../../apis";
import type { DelinquencyState, EcDelinquency } from "../../../apis/endCustomerDelinquency";
import { EmptyState } from "../../../components/billing/EmptyState";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { ShortId } from "../../../components/billing/ShortId";
import { TenantName } from "../../../components/billing/TenantName";
import { TenantSelect } from "../../../components/billing/TenantSelect";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const breadcrumb = [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Билл" },
  { label: "Хугацаа хэтэрсэн" },
];

const STATE_COLORS: Record<string, string> = {
  CURRENT     : "green",
  WARNED      : "yellow",
  BLOCKED     : "orange",
  TRANSFERRED : "red",
};

const STATE_LABEL: Record<string, string> = {
  CURRENT     : "Хэвийн",
  WARNED      : "Сануулсан",
  BLOCKED     : "Хаагдсан",
  TRANSFERRED : "Шилжүүлсэн",
};

const STATE_OPTIONS = Object.entries(STATE_LABEL).map(([value, label]) => ({ value, label }));

const EcDelinquenciesList = () => {
  const [tenantFilter, setTenantFilter] = useState("");
  const [stateFilter, setStateFilter]   = useState<DelinquencyState | null>(null);
  const [running, setRunning]           = useState(false);

  const swrKey = useMemo(
    () => `swr.admin.delinquencies.${tenantFilter}.${stateFilter}`,
    [tenantFilter, stateFilter]
  );
  const { data, mutate } = useSWR(swrKey, () =>
    EndCustomerDelinquencyApi.listAdmin({
      tenantId: tenantFilter || undefined,
      state   : stateFilter ?? undefined,
      page    : 1,
      limit   : 200,
    })
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw              = data as any;
  const rows: EcDelinquency[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);

  const rerun = async () => {
    setRunning(true);
    try {
      await EndCustomerDelinquencyApi.runDelinquency();
      await mutate();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      alert(`Алдаа: ${e?.message ?? e}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <PageLayout
      title="Хугацаа хэтэрсэн харилцагч"
      subTitle="EC шимтгэлийг хугацаандаа төлөөгүй харилцагчдын төлөв"
      breadcrumb={breadcrumb}
      extra={[
        <PermissionGate key="rerun" code="BIL_020">
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            variant="outline"
            loading={running}
            onClick={rerun}>
            Cron гараар ажиллуулах
          </Button>
        </PermissionGate>,
      ]}>
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TenantSelect value={tenantFilter} onChange={setTenantFilter} placeholder="Бүх тенант" width={260} />
          <Select
            placeholder="Бүх төлөв"
            clearable
            w={200}
            data={STATE_OPTIONS}
            value={stateFilter}
            onChange={(v) => setStateFilter((v as DelinquencyState) || null)}
          />
        </Group>

        <Card withBorder radius="md" padding={rows.length === 0 ? "md" : 0}>
          {rows.length === 0 ? (
            <EmptyState icon={<IconCircleCheck size={26} />} message="Хугацаа хэтэрсэн харилцагч байхгүй." />
          ) : (
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Тенант</Table.Th>
                    <Table.Th>Харилцагч</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                    <Table.Th ta="right">Хугацаа хэтэрсэн дүн</Table.Th>
                    <Table.Th>Төлөвт орсон</Table.Th>
                    <Table.Th>Сүүлд сануулсан</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td><TenantName id={d.tenantId} /></Table.Td>
                      <Table.Td><ShortId id={d.endCustomerBusinessId} /></Table.Td>
                      <Table.Td>
                        <Badge color={STATE_COLORS[d.state]} variant="light" radius="sm">
                          {STATE_LABEL[d.state] ?? d.state}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right"><MoneyCell value={d.overdueAmount} /></Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(d.enteredStateAt)}</Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{d.lastReminderAt ? formatDate(d.lastReminderAt) : "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      </Stack>
    </PageLayout>
  );
};

export default EcDelinquenciesList;
